import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import videoM30 from "../assets/videom30.mp4";
import { FaPlay, FaPause, FaVolumeMute, FaVolumeUp } from "react-icons/fa";

const TrainingPrograms = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const sectionRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <Container ref={sectionRef}>
      <GlowBackground />
      <Content>
        <VideoSection $isVisible={isVisible}>
          <VideoWrapper>
            <VideoBorder />
            <Video 
              ref={videoRef}
              muted={isMuted}
              loop
              playsInline
              onClick={togglePlay}
            >
              <source src={videoM30} type="video/mp4" />
              Seu navegador não suporta o vídeo.
            </Video>
            <VideoOverlay $isPlaying={isPlaying} onClick={togglePlay}>
              <PlayButton $isPlaying={isPlaying}>
                <PlayButtonGlow />
                {isPlaying ? <FaPause /> : <FaPlay />}
              </PlayButton>
            </VideoOverlay>
            <VideoControls>
              <ControlButton onClick={toggleMute}>
                {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
              </ControlButton>
            </VideoControls>
            <VideoDecorator $position="top-left" />
            <VideoDecorator $position="bottom-right" />
          </VideoWrapper>
        </VideoSection>

        <TextSection $isVisible={isVisible}>
          <SectionBadge>
            <span>🚀 TRANSFORMAÇÃO</span>
          </SectionBadge>
          
          <Title>
            SEJA A SUA <HighlightText>MELHOR VERSÃO!</HighlightText>
          </Title>
          
          <Subtitle>A MUDANÇA COMEÇA HOJE</Subtitle>
          
          <Description>
            O caminho para o seu melhor desempenho começa com pequenos passos. 
            Desafie seus limites, supere seus obstáculos e descubra o poder 
            que você tem dentro de si. Na M30 Fitness, transformamos vidas 
            através do esporte.
          </Description>

          <FeaturesList>
            <FeatureItem $delay="0.1s" $isVisible={isVisible}>
              <FeatureIcon>💪</FeatureIcon>
              <FeatureText>Treinos personalizados para seu objetivo</FeatureText>
            </FeatureItem>
            <FeatureItem $delay="0.2s" $isVisible={isVisible}>
              <FeatureIcon>🎯</FeatureIcon>
              <FeatureText>Acompanhamento profissional dedicado</FeatureText>
            </FeatureItem>
            <FeatureItem $delay="0.3s" $isVisible={isVisible}>
              <FeatureIcon>⚡</FeatureIcon>
              <FeatureText>Resultados visíveis em semanas</FeatureText>
            </FeatureItem>
          </FeaturesList>

          <ButtonGroup>
            <PrimaryButton href="#precos">
              <ButtonShine />
              <span>COMECE AGORA</span>
            </PrimaryButton>
            <SecondaryButton href="https://wa.me/5531971934978" target="_blank">
              <span>FALE CONOSCO</span>
            </SecondaryButton>
          </ButtonGroup>
        </TextSection>
      </Content>
    </Container>
  );
};

export default TrainingPrograms;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 var(--primary-glow);
  }
  50% {
    transform: scale(1.1);
    box-shadow: 0 0 30px 10px var(--primary-glow);
  }
`;

const shine = keyframes`
  0% {
    left: -100%;
  }
  50%, 100% {
    left: 100%;
  }
`;

const borderGlow = keyframes`
  0%, 100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
`;

const Container = styled.section`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, var(--bg-dark) 0%, #0d0d0d 50%, var(--bg-dark) 100%);
  position: relative;
  padding: 60px 0;
  overflow: hidden;
`;

const GlowBackground = styled.div`
  position: absolute;
  width: 800px;
  height: 800px;
  background: radial-gradient(circle, rgba(229, 168, 0, 0.1) 0%, transparent 70%);
  left: -400px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`;

const Content = styled.div`
  width: 90%;
  max-width: 1200px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 80px;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 50px;
  }
`;

const VideoSection = styled.div`
  flex: 1;
  opacity: 0;
  
  ${({ $isVisible }) => $isVisible && css`
    animation: ${fadeInLeft} 0.8s ease forwards;
  `}
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 550px;
  border-radius: 20px;
  overflow: hidden;
`;

const VideoBorder = styled.div`
  position: absolute;
  inset: -3px;
  background: linear-gradient(135deg, var(--primary-color) 0%, transparent 50%, var(--primary-color) 100%);
  border-radius: 22px;
  z-index: 0;
  animation: ${borderGlow} 3s ease-in-out infinite;
`;

const Video = styled.video`
  position: relative;
  width: 100%;
  height: auto;
  border-radius: 20px;
  cursor: pointer;
  z-index: 1;
`;

const VideoOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, ${({ $isPlaying }) => $isPlaying ? '0' : '0.4'});
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 2;
  border-radius: 20px;
  
  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
`;

const PlayButton = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  background: var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000;
  font-size: 1.5rem;
  transition: all 0.3s ease;
  opacity: ${({ $isPlaying }) => $isPlaying ? '0' : '1'};

  &:hover {
    transform: scale(1.1);
  }

  svg {
    margin-left: ${({ $isPlaying }) => $isPlaying ? '0' : '5px'};
  }
`;

const PlayButtonGlow = styled.div`
  position: absolute;
  inset: -10px;
  background: var(--primary-color);
  border-radius: 50%;
  filter: blur(20px);
  opacity: 0.5;
  z-index: -1;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const VideoControls = styled.div`
  position: absolute;
  bottom: 15px;
  right: 15px;
  z-index: 3;
`;

const ControlButton = styled.button`
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--primary-color);
    color: #000;
  }
`;

const VideoDecorator = styled.div`
  position: absolute;
  width: 100px;
  height: 100px;
  border: 3px solid var(--primary-color);
  z-index: 0;
  opacity: 0.5;
  
  ${({ $position }) => $position === 'top-left' && css`
    top: -20px;
    left: -20px;
    border-right: none;
    border-bottom: none;
    border-radius: 20px 0 0 0;
  `}
  
  ${({ $position }) => $position === 'bottom-right' && css`
    bottom: -20px;
    right: -20px;
    border-left: none;
    border-top: none;
    border-radius: 0 0 20px 0;
  `}
`;

const TextSection = styled.div`
  flex: 1;
  opacity: 0;
  
  ${({ $isVisible }) => $isVisible && css`
    animation: ${fadeInRight} 0.8s ease 0.2s forwards;
  `}

  @media (max-width: 900px) {
    text-align: center;
  }
`;

const SectionBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background: rgba(229, 168, 0, 0.1);
  border: 1px solid rgba(229, 168, 0, 0.2);
  border-radius: 50px;
  padding: 10px 20px;
  margin-bottom: 25px;

  span {
    font-family: 'Rajdhani', sans-serif;
    font-weight: 600;
    font-size: 0.85rem;
    color: var(--primary-color);
    letter-spacing: 3px;
  }
`;

const Title = styled.h2`
  font-family: 'Orbitron', sans-serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  color: #fff;
  line-height: 1.2;
  margin-bottom: 10px;
`;

const HighlightText = styled.span`
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.h3`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--primary-color);
  letter-spacing: 3px;
  margin-bottom: 25px;
`;

const Description = styled.p`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.1rem;
  color: #888;
  line-height: 1.8;
  margin-bottom: 35px;
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 40px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  opacity: 0;
  
  ${({ $isVisible, $delay }) => $isVisible && css`
    animation: ${fadeInUp} 0.5s ease forwards;
    animation-delay: ${$delay};
  `}

  @media (max-width: 900px) {
    justify-content: center;
  }
`;

const FeatureIcon = styled.span`
  font-size: 1.5rem;
`;

const FeatureText = styled.span`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1rem;
  color: #ccc;
  font-weight: 500;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 20px;

  @media (max-width: 600px) {
    flex-direction: column;
  }

  @media (max-width: 900px) {
    justify-content: center;
  }
`;

const PrimaryButton = styled.a`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--gradient-primary);
  color: #000;
  padding: 16px 35px;
  border-radius: 50px;
  font-family: 'Orbitron', sans-serif;
  font-size: 0.9rem;
  font-weight: 700;
  text-decoration: none;
  letter-spacing: 1px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px var(--primary-glow);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px var(--primary-glow);
  }

  span {
    position: relative;
    z-index: 2;
  }
`;

const ButtonShine = styled.div`
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: ${shine} 3s ease-in-out infinite;
`;

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: #fff;
  padding: 16px 35px;
  border-radius: 50px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  font-family: 'Rajdhani', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
    transform: translateY(-3px);
  }
`;
